import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { detectPushEnv } from "@/lib/pushClient";
import { useLang } from "@/lib/i18n";

// Push opt-in card anchored to the EXISTING header bell (see PublicLayout.jsx).
// Shown to unsubscribed visitors (permission === "default") while push is available.
// Dismissal is remembered for the browser session only (sessionStorage).
// It never requests permission itself — the CTA clicks the real bell.
const SESSION_KEY = "bell-card-dismissed-v2"; // v2: earlier test-phase dismissals no longer apply

const HINTS = {
    ar: {
        "ios-browser": "على iPhone/iPad: اضغط زر المشاركة في Safari ثم إضافة إلى الشاشة الرئيسية، وافتح الموقع من الأيقونة الجديدة لتفعيل الإشعارات.",
        "in-app": "أنت تتصفح داخل تطبيق آخر. افتح الموقع في Google Chrome أو Safari لتفعيل الإشعارات (تم نسخ رابط الموقع).",
        "denied": "الإشعارات محظورة لهذا الموقع في المتصفح. لتغيير ذلك: اضغط رمز القفل/الإعدادات بجانب عنوان الموقع ← الإشعارات ← السماح، ثم أعد تحميل الصفحة.",
    },
    en: {
        "ios-browser": "On iPhone/iPad: tap Share in Safari, then Add to Home Screen, and open the site from the new icon to enable notifications.",
        "in-app": "You are browsing inside another app. Open the site in Google Chrome or Safari to enable notifications (the site link has been copied).",
        "denied": "Notifications are blocked for this site in your browser. To change that: tap the lock/settings icon next to the address → Notifications → Allow, then reload the page.",
    },
};

// Who sees the card: every visitor who is NOT actually subscribed on this device.
//   ok                  push supported, permission still "default"
//   granted-unsubscribed permission granted but no live PushSubscription (granted ≠ subscribed)
//   denied              blocked → card explains how to unblock (never re-prompts)
//   ios-browser/in-app  needs Add-to-Home-Screen / a real browser → guidance on click
//   null                subscribed, unsupported, or insecure context → hidden
async function envState() {
    const env = detectPushEnv();
    if (env.kind === "ios-browser" || env.kind === "in-app") return env.kind;
    if (env.kind !== "ok") return null;
    const perm = Notification.permission;
    if (perm === "default") return "ok";
    if (perm === "denied") return "denied";
    try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        return sub ? null : "granted-unsubscribed";
    } catch { return "granted-unsubscribed"; }
}
function dismissed() { try { return !!sessionStorage.getItem(SESSION_KEY); } catch { return false; } }

export default function BellOnboardingCard({ children, cardClass = "", idSuffix = "" }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const { t, dir, lang } = useLang();

    const [kind, setKind] = useState(null);
    useEffect(() => {
        if (dismissed()) return;
        let alive = true;
        const t = setTimeout(async () => {
            const k = await envState();
            if (alive && k && !dismissed()) { setKind(k); setOpen(true); }
        }, 1500);
        return () => { alive = false; clearTimeout(t); };
    }, []);

    // Hide as soon as permission stops being "default" (subscribed or denied).
    useEffect(() => {
        if (!open) return;
        const i = setInterval(async () => { const k = await envState(); if (k === null) setOpen(false); else setKind(k); }, 1500);
        return () => clearInterval(i);
    }, [open]);

    // Keep the card fully inside the viewport with ≥12px side margins: width adapts to the space,
    // the horizontal offset is computed from the bell's position (works for RTL and LTR alike),
    // and the arrow keeps pointing at the bell.
    const [pos, setPos] = useState({ width: 320, left: 0, arrow: 24 });
    useEffect(() => {
        if (!open) return;
        const fit = () => {
            const r = wrapRef.current?.getBoundingClientRect();
            if (!r) return;
            const vw = document.documentElement.clientWidth;
            const width = Math.max(200, Math.min(340, vw - 24));
            const anchor = dir === "rtl" ? r.left : r.right - width;          // card grows away from the bell
            const left = Math.min(Math.max(anchor, 12), vw - 12 - width);
            setPos({ width, left: left - r.left, arrow: Math.min(Math.max(r.left + r.width / 2 - left - 6, 10), width - 22) });
        };
        fit();
        const t1 = setTimeout(fit, 60), t2 = setTimeout(fit, 350);   // header re-flows after a language/direction switch
        window.addEventListener("resize", fit);
        return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener("resize", fit); };
    }, [open, dir, lang]);

    const dismiss = () => { try { sessionStorage.setItem(SESSION_KEY, "1"); } catch {} setOpen(false); window.dispatchEvent(new Event("bell-card:dismiss")); };
    useEffect(() => {   // the desktop and mobile instances share one dismissal
        const onDismiss = () => setOpen(false);
        window.addEventListener("bell-card:dismiss", onDismiss);
        return () => window.removeEventListener("bell-card:dismiss", onDismiss);
    }, []);
    const [hintKind, setHintKind] = useState("");
    const hint = hintKind ? HINTS[lang !== "ar" ? "en" : "ar"][hintKind] : "";   // follows the UI language even if switched while open
    const enable = () => {
        viaCardRef.current = true;
        wrapRef.current?.querySelector('[data-testid="notification-bell"]')?.click(); // existing bell flow (prompt / iOS hint / in-app hint)
        viaCardRef.current = false;
        if (kind === "ok" || kind === "granted-unsubscribed" || kind === null) { dismiss(); return; }
        setHintKind(kind); // keep the card open with guidance instead of vanishing
    };
    const viaCardRef = useRef(false);
    const onWrapCapture = (e) => { if (viaCardRef.current) return; if (open && !e.target.closest?.("[data-bell-card]")) dismiss(); };

    return (
        <span ref={wrapRef} className="relative inline-flex" onClickCapture={onWrapCapture}>
            {children}
            {open && (
                <div role="dialog" aria-label={t("enableNotifications")} dir={dir} data-bell-card=""
                    style={{ width: `${pos.width}px`, left: `${pos.left}px` }}
                    className={`absolute top-full mt-3 z-50 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2 motion-safe:duration-300 ${cardClass}`}
                    data-testid={`bell-card${idSuffix}`}>
                    <span aria-hidden="true" style={{ left: `${pos.arrow}px` }} className="absolute -top-1.5 h-3 w-3 rotate-45 bell-ring-static rounded-[2px] opacity-90" />
                    <div className="bell-ring rounded-[14px] p-[1.5px] shadow-lg shadow-indigo-900/10 dark:shadow-black/50">
                        <div className="relative rounded-[12.5px] bg-white dark:bg-[#18181b] p-4 pe-9 text-start">
                            <button type="button" onClick={dismiss} aria-label={t("close")}
                                className="absolute top-2 end-2 h-7 w-7 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-white/10 transition-colors"
                                data-testid={`bell-card-close${idSuffix}`}>
                                <X className="w-4 h-4" />
                            </button>
                            <p className="font-sans text-[15px] leading-[1.9] text-gray-900 dark:text-gray-100" data-testid={`bell-card-text${idSuffix}`}>
                                {t("bellCardText")} <span className="font-bold text-gray-950 dark:text-white">{t("siteName")}</span>{dir === "rtl" ? "، بإشعارات تصلك مباشرة على جهازك." : " — notifications delivered straight to your device."}
                            </p>
                            {hint && (
                                <p className="mt-2 text-[13px] leading-[1.8] text-gray-700 dark:text-gray-300 border-t border-gray-200 dark:border-white/10 pt-2" data-testid={`bell-card-hint${idSuffix}`}>{hint}</p>
                            )}
                            {/* Buttons sit side by side only when both fit; otherwise they stack full-width. Never clipped, never truncated. */}
                            <div className="mt-3 flex flex-wrap items-stretch gap-2" data-testid={`bell-card-actions${idSuffix}`}>
                                <span className="flex-[1_1_150px] min-w-0 bell-ring-static rounded-[10px] p-[2px] shadow-md shadow-indigo-900/20">
                                    <button type="button" onClick={enable}
                                        className="w-full h-full min-h-[44px] inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-red hover:bg-brand-red-dark active:bg-red-800 text-white font-display font-bold text-[14px] sm:text-[15px] leading-snug text-center px-3 py-2 transition-colors"
                                        data-testid={`bell-card-enable${idSuffix}`}>
                                        <span aria-hidden="true">🔔</span> <span>{t("bellCardEnable")}</span>
                                    </button>
                                </span>
                                <button type="button" onClick={dismiss}
                                    className="flex-[1_1_96px] min-w-0 min-h-[44px] px-3 py-2 leading-snug text-center rounded-[10px] border-[1.5px] border-indigo-300 dark:border-indigo-400/50 bg-white dark:bg-white/5 text-sm font-bold text-gray-700 hover:text-gray-900 hover:bg-indigo-50 dark:text-gray-200 dark:hover:text-white dark:hover:bg-white/10 transition-colors"
                                    data-testid={`bell-card-later${idSuffix}`}>
                                    {t("notNow")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </span>
    );
}
